import { safeArray, stringToCheck, wildcardFilter } from './resultsHelper';

const splitPrefix = (term) => {
  const colon = term.indexOf(':');
  return colon > 0 ? [term.slice(0, colon), term.slice(colon + 1)] : [term];
};

const suggestionKeys = (suggestions) => safeArray(suggestions)
  .filter((s) => typeof s === 'string');

export const wildCardFilterWithSuggestions = (filter, suggestions) => {
  const keys = suggestionKeys(suggestions);
  const [key, value = ''] = splitPrefix(stringToCheck(filter));
  const prefix = key.trim().toLowerCase();
  const typed = value.trim();
  return (typed && keys.includes(prefix))
    ? `${prefix}: ${wildcardFilter(typed)}`
    : wildcardFilter(stringToCheck(filter));
};

export const sanitiseFilterSuggestion = (value, suggestions = []) => {
  const keys = suggestionKeys(suggestions);
  const index = stringToCheck(value).indexOf(':');
  const filter = index > 0 && index + 1 < value.length && value[index + 1] !== ' '
    ? `${value.slice(0, index + 1)} ${value.slice(index + 1)}`
    : value;
  const [key, rest] = splitPrefix(stringToCheck(filter));
  const lower = key.toLowerCase();
  const isOnlyPrefix = (rest !== undefined && !rest.trim()
      && keys.some((s) => s.toLowerCase().startsWith(lower)))
    || (rest === undefined && !!key
      && keys.some((s) => s.toLowerCase().startsWith(lower)));

  return { filter, isOnlyPrefix };
};

const filteredSuggestion = (key, value, label) => ({
  key,
  prefix: `${key}:`,
  displayValue: value,
  label,
  value: value ? `${key}: ${value}` : `${key}:`,
});

const exactKeySuggestions = (lower, suggestions, label) => {
  const exact = suggestions.find((key) => key.toLowerCase() === lower.replace(/:$/, ''));
  return exact ? [filteredSuggestion(exact, undefined, label)] : undefined;
};

const byProximity = (lower) => (a, b) => {
  const rank = (key) => (key.toLowerCase().startsWith(lower) ? 0 : 1);
  return rank(a) - rank(b) || a.toLowerCase().localeCompare(b.toLowerCase());
};

const knownPrefixSuggestions = (term, suggestions, label) => {
  const [key, rest] = splitPrefix(term);
  if (rest === undefined) { return undefined; }

  const matched = suggestions.find((s) => s.toLowerCase() === key.toLowerCase());
  if (!matched) { return undefined; }

  const typedValue = rest.trim();
  if (!typedValue) { return undefined; }

  // offer every prefix carrying the typed value, closest matches first
  return [...suggestions].sort(byProximity(key))
    .map((s) => filteredSuggestion(s, typedValue, label));
};

const partialKeySuggestions = (lower, suggestions, label) => {
  if (lower.includes(':')) { return undefined; }
  if (!suggestions.some((key) => key.toLowerCase().startsWith(lower))) { return undefined; }
  return [...suggestions].sort(byProximity(lower))
    .map((key) => filteredSuggestion(key, undefined, label));
};

const freeTextSuggestions = (term, suggestions, label) => {
  const [key, rest] = splitPrefix(term);
  const knownPrefix = rest !== undefined
    && suggestions.some((s) => s.toLowerCase() === key.toLowerCase());
  const displayValue = knownPrefix ? rest.trim() : term;

  if (!displayValue) { return undefined; }
  return suggestions.map((s) => filteredSuggestion(s, displayValue, label));
};

const filteredSuggestions = (value, suggestions = [], label) => {
  const keys = suggestionKeys(suggestions);
  if (!keys.length) { return undefined; }
  const term = stringToCheck(value);

  if (!term) {
    return keys.map((key) => filteredSuggestion(key, undefined, label));
  }

  return exactKeySuggestions(term.toLowerCase(), keys, label)
    || knownPrefixSuggestions(term, keys, label)
    || partialKeySuggestions(term.toLowerCase(), keys, label)
    || freeTextSuggestions(term, keys, label);
};

export default filteredSuggestions;
